import { Link } from "@tanstack/react-router";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { cn } from "@/lib/utils";

const logoVariants = cva("flex items-center", {
	variants: {
		size: {
			sm: "gap-2",
			md: "gap-2.5",
			lg: "gap-3",
			xl: "gap-4",
		},
	},
	defaultVariants: {
		size: "md",
	},
});

const logoTextVariants = cva("font-mono font-normal tracking-tight", {
	variants: {
		size: {
			sm: "text-base",
			md: "text-lg",
			lg: "text-xl",
			xl: "text-2xl",
		},
	},
	defaultVariants: {
		size: "md",
	},
});

const logoIconVariants = cva("shrink-0 rounded-[22%]", {
	variants: {
		size: {
			sm: "size-6",
			md: "size-7",
			lg: "size-8",
			xl: "size-10",
		},
	},
	defaultVariants: {
		size: "md",
	},
});

export interface LogoProps
	extends React.HTMLAttributes<HTMLDivElement>,
		VariantProps<typeof logoVariants> {
	showIcon?: boolean;
	showText?: boolean;
	showDomain?: boolean;
	disableLink?: boolean;
	ref?: React.Ref<HTMLDivElement>;
}

function Logo({
	className,
	size = "md",
	showIcon = true,
	showText = true,
	showDomain = false,
	disableLink = false,
	ref,
	...props
}: LogoProps) {
	const content = (
		<>
			{showIcon && (
				<img
					src="/app-icon.svg"
					alt=""
					aria-hidden="true"
					className={cn(logoIconVariants({ size }))}
				/>
			)}
			{showText && (
				<span className={cn(logoTextVariants({ size }), "text-foreground")}>
					AgentMRR
					{showDomain && <span className="text-muted-foreground">.com</span>}
				</span>
			)}
		</>
	);

	const wrapperClass = cn(logoVariants({ size }), className);

	if (disableLink) {
		return (
			<div ref={ref} className={wrapperClass} {...props}>
				{content}
			</div>
		);
	}

	return (
		<Link to="/" className={cn(wrapperClass, "cursor-pointer")}>
			{content}
		</Link>
	);
}

export { Logo };
